import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Menu, 
  User as UserIcon,
  LogOut,
  Bell,
  BellDot,
  Bookmark,
  HeartPulse,
  LifeBuoy,
  LogIn,
  UserPlus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getNavigationItems } from '@/utils/navigationItems';
import { getUserNotifications } from '@/utils/notificationsUtils';
import { detectSpecialAdminEmails } from '@/utils/adminUtils';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Memoize admin check to prevent re-calculations
  const isAdmin = useMemo(() => {
    if (!user?.email) {
      console.log('🔍 [Navbar] No user email available');
      return false;
    }
    
    console.log('🔍 [Navbar] Checking admin status for:', user.email);
    console.log('🔍 [Navbar] User metadata role:', (user as any)?.user_metadata?.role);
    console.log('🔍 [Navbar] User role:', user?.role);
    
    const isSpecialAdmin = detectSpecialAdminEmails(user.email);
    console.log('🔍 [Navbar] Is special admin:', isSpecialAdmin);
    
    const result = (user as any)?.user_metadata?.role === 'admin' || 
                   user?.role === 'admin' || 
                   isSpecialAdmin;
    
    console.log('🔍 [Navbar] Final admin result:', result);
    return result;
  }, [user?.email, (user as any)?.user_metadata?.role, user?.role]);
  
  const navItems = useMemo(() => {
    console.log('🔍 [Navbar] Getting navigation items with isAdmin:', isAdmin);
    return getNavigationItems(isAdmin);
  }, [isAdmin]);

  const isActiveLink = (href) => {
    if (href === '/' && location.pathname !== '/') return false;
    // Don't mark external links as active
    if (href.startsWith('http')) return false;
    return location.pathname.startsWith(href);
  };

  useEffect(() => {
    if (user?.id) {
      const fetchNotifications = async () => {
        try {
          const userNotifications = await getUserNotifications(user.id);
          setNotifications(userNotifications);
          
          const unread = userNotifications.filter(notification => !notification.is_read).length;
          setUnreadCount(unread);
        } catch (error) {
          console.error('Error fetching notifications:', error);
        }
      };
      
      fetchNotifications();
      
      const intervalId = setInterval(fetchNotifications, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Memoize user display data to prevent re-calculations
  const userDisplayData = useMemo(() => {
    if (!user) return null;
    
    return {
      userName: (user as any)?.user_metadata?.name || (user as any)?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Usuario',
      userAvatar: (user as any)?.user_metadata?.avatar_url || (user as any)?.user_metadata?.picture
    };
  }, [user]);

  if (!user) {
    return (
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/lovable-uploads/c0f6fe53-8b80-4c8d-b59a-d556679f7965.png" 
                alt="Guía Vegana" 
                className="h-10"
              />
            </Link>

            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  <span>Iniciar sesión</span>
                </Button>
              </Link>
              <Link>
                <Button variant="default" size="sm" className="flex items-center gap-2 bg-vegana-primary hover:bg-vegana-primary/90">
                  <UserPlus className="h-4 w-4" />
                  <span>Registrarse</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/c0f6fe53-8b80-4c8d-b59a-d556679f7965.png" 
              alt="Guía Vegana" 
              className="h-10"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => {
              // Special styling for Tutoriales
              const isTutorialesLink = item.title === 'Tutoriales';
              
              // Create either a regular Link or an external <a> based on the item
              const NavElement = item.isExternal 
                ? (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-2 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors
                      ${item.bgColor ? 'text-white' : (isActiveLink(item.href)
                        ? 'text-vegana-primary bg-vegana-primary/10'
                        : 'text-gray-600 hover:text-vegana-primary hover:bg-vegana-primary/5')
                      } ${item.highlight ? 'bg-amber-100 hover:bg-amber-200' : ''}
                      ${isTutorialesLink ? 'bg-green-100 hover:bg-green-200 text-green-800' : ''}`}
                    style={item.bgColor ? { backgroundColor: item.bgColor } : {}}
                  >
                    <item.icon className={`w-4 h-4 ${item.highlight ? 'text-amber-600' : item.bgColor ? 'text-white' : isTutorialesLink ? 'text-green-700' : ''}`} />
                    <span>{item.title}</span>
                  </a>
                )
                : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`px-2 py-2 text-sm font-medium rounded-md flex items-center gap-1 transition-colors
                      ${isActiveLink(item.href)
                        ? 'text-vegana-primary bg-vegana-primary/10'
                        : 'text-gray-600 hover:text-vegana-primary hover:bg-vegana-primary/5'
                      } ${item.highlight ? 'bg-amber-100 hover:bg-amber-200' : ''}
                      ${isTutorialesLink ? 'bg-green-100 hover:bg-green-200 text-green-800' : ''}`}
                  >
                    <item.icon className={`w-4 h-4 ${item.highlight ? 'text-amber-600' : isTutorialesLink ? 'text-green-700' : ''}`} />
                    <span>{item.title}</span>
                  </Link>
                );
                
              return NavElement;
            })}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative">
                  {unreadCount > 0 ? (
                    <>
                      <BellDot className="h-5 w-5 text-vegana-primary" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    </>
                  ) : (
                    <Bell className="h-5 w-5" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex justify-between items-center">
                  <span>Notificaciones</span>
                  <Link to="/notificaciones" className="text-xs text-vegana-primary hover:underline">
                    Ver todas
                  </Link>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {notifications.length > 0 ? (
                  notifications.slice(0, 5).map((notification) => (
                    <DropdownMenuItem key={notification.id} className={`py-3 ${!notification.read ? 'bg-vegana-primary/5' : ''}`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${!notification.read ? 'bg-vegana-primary' : 'bg-gray-200'}`} />
                        <div className="flex-1">
                          <div className="text-sm font-medium">{notification.title}</div>
                          <div className="text-xs text-gray-500">{notification.message}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No tienes notificaciones
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/favoritos">
              <Button variant="ghost" size="sm">
                <Bookmark className="h-5 w-5 text-blue-500 hover:text-blue-600" />
              </Button>
            </Link>
          
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userDisplayData?.userAvatar} alt={userDisplayData?.userName} />
                    <AvatarFallback className="bg-vegana-primary text-white">
                      {userDisplayData?.userName.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{userDisplayData?.userName}</p>
                    <p className="text-xs leading-none text-gray-500">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/perfil')}>
                  <UserIcon className="mr-2 h-4 w-4" />
                  <span>Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/nutricion')}>
                  <HeartPulse className="mr-2 h-4 w-4" />
                  <span>Características</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/soporte')}>
                  <LifeBuoy className="mr-2 h-4 w-4" />
                  <span>Soporte</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Cerrar sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden ml-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden py-2 px-4 bg-white border-t border-gray-100"
        >
          <nav className="flex flex-col space-y-1">
            {navItems.map((item) => {
              // Special styling for Tutoriales in mobile
              const isTutorialesLink = item.title === 'Tutoriales';
              
              return item.isExternal ? (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-2 text-base font-medium rounded-md flex items-center gap-2
                    ${item.bgColor ? 'text-white' : 'text-gray-600 hover:text-vegana-primary hover:bg-vegana-primary/5'} 
                    ${item.highlight ? 'bg-amber-100 hover:bg-amber-200' : ''}
                    ${isTutorialesLink ? 'bg-green-100 hover:bg-green-200 text-green-800' : ''}`}
                  style={item.bgColor ? { backgroundColor: item.bgColor } : {}}
                >
                  <item.icon className={`w-5 h-5 ${item.highlight ? 'text-amber-600' : item.bgColor ? 'text-white' : isTutorialesLink ? 'text-green-700' : ''}`} />
                  <span>{item.title}</span>
                </a>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-2 text-base font-medium rounded-md flex items-center gap-2
                    ${isActiveLink(item.href)
                      ? 'text-vegana-primary bg-vegana-primary/10 font-semibold'
                      : 'text-gray-600 hover:text-vegana-primary hover:bg-vegana-primary/5'
                    } ${item.highlight ? 'bg-amber-100 hover:bg-amber-200' : ''}
                    ${isTutorialesLink ? 'bg-green-100 hover:bg-green-200 text-green-800' : ''}`}
                >
                  <item.icon className={`w-5 h-5 ${item.highlight ? 'text-amber-600' : isTutorialesLink ? 'text-green-700' : ''}`} />
                  <span>{item.title}</span>
                </Link>
              );
            })}
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Navbar;
